import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import BrandLogo from '../components/common/BrandLogo';
import TelemetryIllustration from '../components/common/TelemetryIllustration';

const { FiTarget, FiActivity, FiZap, FiChevronRight, FiChevronDown, FiCpu, FiGlobe } = FiIcons;

const SystemIntel = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);

  const steps = [
    { title: "Unit Initialization", desc: "Register vehicles via encrypted OBD-II pairing. System automatically syncs VIN and diagnostic headers.", type: "engine" },
    { title: "Vector Mapping", desc: "AI-driven geofencing defines high-density delivery polygons to minimize cross-traffic latency.", type: "map" },
    { title: "Telemetry Sync", desc: "Near-zero latent polling (5s) streams engine vitals, fuel levels, and tire pressure to the Command Core.", type: "engine" },
    { title: "Command Dispatch", desc: "Autonomous routing pushes dynamic mission updates directly to driver HUD terminals.", type: "report" }
  ];

  const faqs = [
    { q: "How is data security handled?", a: "FleetForesight utilizes end-to-end AES-256 encryption. All telemetry data is siloed per organization and scrubbed of PII outside of operational windows." },
    { q: "Does it support electric fleets?", a: "Yes. Our power monitoring module integrates directly with major EV battery management systems for precise range forecasting." },
    { q: "What is the hardware footprint?", a: "Minimal. A standard cellular-enabled OBD-II dongle provides all the necessary data vectors for full platform functionality." },
    { q: "Can we integrate with legacy ERPs?", a: "Our GraphQL API enables seamless data bridging with existing logistics and accounting suites." },
    { q: "How does the AI handle rural routes?", a: "The 'Predictor' model adjusts for low-connectivity zones by caching mission data and optimizing for fuel stops rather than density." }
  ];

  return (
    <div className="space-y-12 max-w-6xl mx-auto pb-24">
      <section className="hud-panel p-10 relative overflow-hidden group">
        <div className="absolute top-0 right-0 p-12 opacity-5 scale-150 group-hover:scale-125 transition-transform duration-1000">
          <BrandLogo size="lg" showText={false} />
        </div>
        <div className="relative z-10 space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00f0ff]/5 border border-[#00f0ff]/20 text-[10px] font-mono text-[#00f0ff] uppercase tracking-widest">
            <SafeIcon icon={FiGlobe} /> Global Operations Manual
          </div>
          <h2 className="text-4xl font-bold text-white tracking-tight leading-none">
            ENGINEERING <span className="text-[#00f0ff] text-glow-blue">FORESIGHT</span>
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl leading-relaxed">
            The mission-critical command center for local delivery and service fleets. 
            We bridge the gap between static logistics and real-time operational reality.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-6">
            {[ 
              { icon: FiTarget, label: "Precision Routing", sub: "18% Avg. Fuel Savings", color: "text-[#00f0ff]" },
              { icon: FiZap, label: "Zero Downtime", sub: "Predictive Health Monitoring", color: "text-[#ff5500]" },
              { icon: FiActivity, label: "Live Operations", sub: "100% Fleet Visibility", color: "text-green-500" }
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-4">
                <div className={`p-3 bg-white/5 rounded-xl border border-white/10 ${item.color}`}><SafeIcon icon={item.icon} className="text-xl" /></div>
                <div>
                  <p className="text-xs font-bold uppercase text-white tracking-wider">{item.label}</p>
                  <p className="text-[10px] text-gray-500 font-mono">{item.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
        <div className="lg:col-span-2 space-y-4">
          <h3 className="text-xs font-mono text-[#00f0ff] uppercase tracking-[0.3em] mb-6 flex items-center gap-2">
            <div className="w-1 h-4 bg-[#00f0ff]"></div> MISSION_FLOW
          </h3>
          <div className="space-y-3">
            {steps.map((step, idx) => (
              <button 
                key={idx}
                onClick={() => setActiveStep(idx)}
                className={`w-full text-left p-4 rounded-xl border transition-all duration-300 flex items-center justify-between group ${activeStep === idx ? 'bg-[#00f0ff]/10 border-[#00f0ff]/40 shadow-[0_0_20px_rgba(0,240,255,0.05)]' : 'bg-[#0a0a12] border-[#1a1a2e] hover:border-gray-700'}`}
              >
                <div className="flex items-center gap-4">
                  <span className={`text-[10px] font-mono ${activeStep === idx ? 'text-[#00f0ff]' : 'text-gray-600'}`}>0{idx + 1}</span>
                  <span className={`text-xs font-bold uppercase tracking-widest ${activeStep === idx ? 'text-white' : 'text-gray-500 group-hover:text-gray-300'}`}>{step.title}</span>
                </div>
                <SafeIcon icon={FiChevronRight} className={`${activeStep === idx ? 'text-[#00f0ff]' : 'text-gray-800'}`} />
              </button>
            ))}
          </div>
        </div>
        
        <div className="lg:col-span-3">
          <div className="hud-panel p-1 border-white/5 h-full min-h-[400px]">
            <div className="h-full w-full bg-[#050508] rounded-lg relative flex flex-col overflow-hidden">
              <div className="p-4 border-b border-[#1a1a2e] flex justify-between items-center bg-[#0a0a12]/50">
                <span className="text-[10px] font-mono text-gray-500 uppercase tracking-widest">Live_Simulation_Terminal</span>
                <div className="flex gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-red-500/20 border border-red-500/40"></div>
                  <div className="w-2 h-2 rounded-full bg-yellow-500/20 border border-yellow-500/40"></div>
                  <div className="w-2 h-2 rounded-full bg-green-500/20 border border-green-500/40"></div>
                </div>
              </div>
              <div className="flex-1 p-8 flex flex-col items-center justify-center text-center space-y-6">
                <AnimatePresence mode="wait">
                  <motion.div 
                    key={activeStep}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.05 }}
                    className="w-full max-w-sm space-y-6"
                  >
                    <div className="h-48 w-full">
                      <TelemetryIllustration type={steps[activeStep].type} />
                    </div>
                    <div className="space-y-2">
                      <h4 className="text-xl font-bold text-white uppercase tracking-tight">{steps[activeStep].title}</h4>
                      <p className="text-sm text-gray-500 leading-relaxed font-mono uppercase tracking-wide">{steps[activeStep].desc}</p>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
              <div className="p-4 border-t border-[#1a1a2e] bg-[#0a0a12]/50 text-[9px] font-mono text-[#00f0ff]/40 flex justify-between uppercase">
                <span>Status: Optimal</span>
                <span>Buffer: 0.002ms</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="space-y-8">
        <div className="text-center space-y-2">
          <h3 className="text-xs font-mono text-gray-500 uppercase tracking-[0.4em]">Operations FAQ</h3>
          <h2 className="text-3xl font-bold text-white tracking-tight uppercase">System <span className="text-[#ff5500] text-glow-orange">Diagnostics</span></h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {faqs.map((faq, idx) => (
            <div key={idx} className="hud-panel overflow-hidden border-white/5 hover:border-white/10 transition-colors">
              <button 
                onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                className="w-full p-6 flex justify-between items-center text-left"
              >
                <span className="text-xs font-bold text-gray-300 uppercase tracking-widest">{faq.q}</span>
                <SafeIcon icon={openFaq === idx ? FiChevronDown : FiChevronRight} className="text-[#00f0ff]" />
              </button>
              {openFaq === idx && (
                <div className="px-6 pb-6 text-[10px] text-gray-500 leading-relaxed font-mono uppercase tracking-wide border-t border-[#1a1a2e] pt-4 bg-[#050508]/50">
                  {faq.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <footer className="pt-16 pb-8 border-t border-[#1a1a2e] flex flex-col items-center gap-4">
        <BrandLogo size="sm" />
        <p className="text-[9px] font-mono text-gray-600 uppercase tracking-[0.5em]">
          Made by LingLink AI and DigLab AI
        </p>
      </footer>
    </div>
  );
};

export default SystemIntel;